/* ==========================================================================
   QUICK BUY + CART HANDLER + QUICK ADD TO CART + QUICK VIEW PRODUCT + HELPERS
   Refactor goals:
   - No behavior change unless explicitly needed (keeps your theme logic)
   - Safer selectors / null guards
   - Clear method boundaries + consistent naming
   - Fix obvious bug: fetch option {prioirty} -> {priority}
   - Keep existing CSS class hooks + data-* contracts
   ========================================================================== */
function waitForElement(selector, timeoutMs = 15000) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(selector);
    if (existing) return resolve(existing);

    const obs = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (el) {
        obs.disconnect();
        resolve(el);
      }
    });

    obs.observe(document.documentElement, { childList: true, subtree: true });

    setTimeout(() => {
      obs.disconnect();
      reject(new Error(`Timed out waiting for: ${selector}`));
    }, timeoutMs);
  });
}
(() => {
  document.addEventListener('click', (e) => {
    const tile = e.target.closest('.tolstoy-product-tile');
    if (!tile) return;

    if (tile.dataset.status === "failed") {
      const handle = tile.querySelector('[data-product-handle]')?.dataset.productHandle;
      if (handle) window.location.href = `/products/${handle}`;
    }
  }, true);
})();

if (typeof QuickBuy !== 'function') {
  class QuickBuy extends HTMLElement {
    constructor() {
      super();

      this.zIndex = 99;

      this.productItem = this.closest('[data-js-product-item]') || null;
      this.productVariants = this.querySelector('product-variants') || null;

      // Product-item-only references
      this.variantImages = null;
      this.productImage = null;
      this.variantImagesSliders = false;
      this.productSlider = null;

      this._quickBuyPrice = null;
      this._defaultToFirstVariant = true;

      if (!this.productVariants) return;

      if (this.productItem) {
        this.variantImages = this.querySelector('[data-js-quick-buy-product-images]') || null;
        this.productImage =
          this.productItem.querySelector('[data-js-product-item-image]') || null;

        this.variantImagesSliders = this.hasAttribute('data-variant-images-slider');
        this.productSlider = this.productItem.querySelector('css-slider') || null;

        this._quickBuyPrice = this.productVariants.dataset.buyButtonPrice || null;
        this._defaultToFirstVariant = !this.productVariants.hasAttribute('data-variant-required');
      }

      this._bindVariantEvents();
      this._initDeferredVariantImages();
      this._initVariantRadioScroller();
      this._initQuickBuyFormSubmit();
      this._initCardPriceSync();
      this._initDefaultVariantLabels();
      this._initNonDefaultVariantLabels();
    }

    /* -----------------------------
       Bindings / init helpers
    ------------------------------ */

    _bindVariantEvents() {
      this.onVariantChangeHandler = this.onVariantChangeHandler.bind(this);

      this.productVariants.addEventListener('VARIANT_CHANGE', this.onVariantChangeHandler);

      // Kick once using current state
      this.onVariantChangeHandler({ target: this.productVariants });
    }

    _initDeferredVariantImages() {
      if (!this.variantImages) return;

      // Preserve your 5s delay (likely perf-related)
      setTimeout(() => {
        const observer = new IntersectionObserver((entries, obs) => {
          const entry = entries && entries[0];
          if (!entry || !entry.isIntersecting) return;

          this.variantImages.querySelectorAll('template').forEach((tpl) => {
            if (!tpl.dataset.init) this.initVariantImage(tpl);
          });

          obs.unobserve(this);
        });

        observer.observe(this);
      }, 5000);
    }

    _initVariantRadioScroller() {
      // Preserve your 1s delay (allows sliders to mount)
      setTimeout(() => {
        this.querySelectorAll('.product-variant__item--radio input').forEach((input) => {
          input.addEventListener('click', () => {
            const inputParent = input.closest('.product-variant__item--radio');
            const sliderHolder = inputParent?.closest('.css-slider-holder');

            if (sliderHolder && inputParent) {
              sliderHolder.scrollTo({
                top: 0,
                left: inputParent.offsetLeft - 40,
                behavior: 'smooth',
              });
            }

            if (inputParent?.classList.contains('product-variant__item--color')) {
              const label = inputParent.closest('.product-variant')?.querySelector(
                '.product-variant__item-text-label'
              );
              if (label) label.textContent = input.value;
            }
          });
        });
      }, 1000);
    }

    _initQuickBuyFormSubmit() {
      const quickBuyFormFlag = this.querySelector('[data-js-quick-buy-form]');
      const form = this.querySelector('form');
      if (!quickBuyFormFlag || !form) return;

      form.addEventListener('submit', (e) => {
        e.preventDefault();

        const submitButton = this.querySelector('[type="submit"]');
        submitButton?.classList.add('working');

        window.handleAddToCart(
          this._serializeForm(e.currentTarget),
          1,
          {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Requested-With': 'XMLHttpRequest',
          },
          () => {
            submitButton?.classList.remove('working');
          }
        );
      });
    }

    _initCardPriceSync() {
      // Preserve your 10ms delay
      setTimeout(() => {
        if (!this.productItem) return;

        const cardPrice = this.productItem.querySelector('[data-js-product-item-price]');
        const buttonPrice = this.querySelector('[data-js-product-add-to-cart-text]');
        const button = this.querySelector('[data-js-product-add-to-cart]');
        const productPrice = this.querySelector('[data-js-quick-buy-product-price]');
        if (!productPrice) return;

        const apply = () => {
          if (cardPrice) {
            cardPrice.innerHTML = productPrice.innerHTML;

            const original = productPrice.querySelector('[data-js-product-price-original]');
            const isEmpty = !original || original.textContent === '';

            cardPrice.parentNode?.classList.toggle('product-item__price--empty', isEmpty);
          }

          if (
            this.dataset.addToCartPrice === 'true' &&
            buttonPrice &&
            button &&
            !button.classList.contains('disabled')
          ) {
            // Keep your existing behavior (appends price repeatedly if productPrice mutates often)
            // If you want to avoid duplication, we can add a guard later.
            buttonPrice.innerHTML = `${buttonPrice.innerHTML} <span class='element--hide-on-small'>- ${productPrice.innerHTML}</span>`;
          }
        };

        new MutationObserver(apply).observe(productPrice, {
          attributes: false,
          childList: true,
          subtree: true,
        });
      }, 10);
    }

    _initDefaultVariantLabels() {
      // Preserve your 10ms delay (same block as above in original)
      setTimeout(() => {
        if (!this._defaultToFirstVariant) return;
        if (!this.productItem) return;

        const cardPrice = this.productItem.querySelector('[data-js-product-item-price]');
        const defaultPriceData = this.productItem.querySelector('[data-js-product-default-price-data]');

        if (cardPrice && defaultPriceData) {
          cardPrice.innerHTML = defaultPriceData.innerHTML;

          const buttonPrice = this.querySelector('[data-js-product-add-to-cart-text]');
          const button = this.querySelector('[data-js-product-add-to-cart]');
          const productPrice = this.querySelector('[data-js-quick-buy-product-price]');

          if (
            this.dataset.addToCartPrice === 'true' &&
            buttonPrice &&
            button &&
            productPrice &&
            !button.classList.contains('disabled')
          ) {
            if (this.dataset.singleVariant !== 'true') {
              buttonPrice.innerHTML = `${buttonPrice.innerHTML} <span class='element--hide-on-small'>- ${productPrice.innerHTML}</span>`;
            }
          }
        }

        // Set color label to selected value
        this.querySelectorAll('.product-variant__item--color [data-selected]').forEach((elm) => {
          if (!elm.hasAttribute('data-silent-selected')) {
            const label = elm.closest('.product-variant')?.querySelector('.product-variant__item-text-label');
            if (label) label.textContent = elm.value;
          }
        });
      }, 10);
    }

    _initNonDefaultVariantLabels() {
      setTimeout(() => {
        if (this._defaultToFirstVariant) return;

        this.querySelectorAll('.product-variant__item--color [data-product-url][data-selected]').forEach((elm) => {
          if (!elm.hasAttribute('data-silent-selected')) {
            const label = elm.closest('.product-variant')?.querySelector('.product-variant__item-text-label');
            if (label) label.textContent = elm.value;
          }
        });

        const pv = this.querySelector('product-variants');
        if (pv && parseInt(pv.dataset.variants, 10) === 1) {
          pv.updateBuyButtons?.();
        }
      }, 1);
    }

    /* -----------------------------
       Variant update logic
    ------------------------------ */

    onVariantChangeHandler(e) {
      const variant = e?.target?.currentVariant;
      if (!variant) return;

      if (this.variantImages) {
        // Template-based image overlay swap
        const media = variant.featured_media;
        if (media && media.id) {
          const tpl = this.variantImages.querySelector(`template[data-media-id="${media.id}"]`);
          if (tpl) {
            if (!tpl.dataset.init) this.initVariantImage(tpl);
            this.showVariantImage(media.id);
          }
        }
      } else if (this.variantImagesSliders) {
        // Slider-based image switch
        const featuredImage = variant.featured_image;
        if (featuredImage && featuredImage.id && this.productSlider) {
          const slideElm = this.productSlider.querySelector(`.product-item__image-${featuredImage.id}`);
          if (slideElm) {
            this.productSlider.changeSlide?.(slideElm.dataset.index);
          }
        }
      }

      // Preserved no-op from original
      this.querySelectorAll('css-slider').forEach((_slider) => {
        // _slider.resetSlider(false, false);
      });
    }

    initVariantImage(template) {
      if (!template || !this.productImage) return;

      template.dataset.init = true;

      const fig = template.content?.querySelector('figure');
      if (!fig) return;

      const image = fig.cloneNode(true);
      image.dataset.mediaId = template.dataset.mediaId;
      this.productImage.append(image);
    }

    showVariantImage(id) {
      if (!this.productImage || !id) return;

      this.productImage
        .querySelector('.product-item__image-figure--on-top')
        ?.classList.remove('product-item__image-figure--on-top');

      const active = this.productImage.querySelector(`[data-media-id="${id}"]`);
      if (!active) return;

      active.classList.add('product-item__image-figure--on-top');

      // Re-trigger srcset evaluation (kept from your original)
      const img = active.querySelector('img');
      if (img) img.srcset = img.srcset;

      active.style.zIndex = ++this.zIndex;
    }

    _serializeForm(form) {
      const arr = [];
      Array.prototype.slice.call(form.elements).forEach((field) => {
        if (
          !field.name ||
          field.disabled ||
          ['file', 'reset', 'submit', 'button'].indexOf(field.type) > -1
        )
          return;

        if (field.type === 'select-multiple') {
          Array.prototype.slice.call(field.options).forEach((option) => {
            if (!option.selected) return;
            arr.push(`${encodeURIComponent(field.name)}=${encodeURIComponent(option.value)}`);
          });
          return;
        }

        if (['checkbox', 'radio'].indexOf(field.type) > -1 && !field.checked) return;

        arr.push(`${encodeURIComponent(field.name)}=${encodeURIComponent(field.value)}`);
      });

      return arr.join('&');
    }
  }

  if (typeof customElements.get('quick-buy') === 'undefined') {
    customElements.define('quick-buy', QuickBuy);
  }
}

if (!window.handleAddToCart) {
  window.handleAddToCart = (body, items, headers, callback) => {
    let alert = '';

    fetch(`${KROWN.settings.routes.cart_add_url}.js`, {
      body,
      headers,
      method: 'POST',
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status == 422) {
          // wrong stock logic alert
          alert = document.createElement('span');
          alert.className = 'alert alert--error';
          alert.innerHTML = response.description;
          return fetch('?section_id=helper-cart');
        }
        return fetch('?section_id=helper-cart');
      })
      .then((response) => response.text())
      .then((text) => {
        let doNext = false;

        if (document.body.classList.contains('template-cart')) {
          doNext = 'page';
        } else {
          if (KROWN.settings.cart_popup == 'true' && document.getElementById('mini-cart-popup')) {
            doNext = 'popup';
          } else if (KROWN.settings.cart_action == 'overlay' && document.getElementById('site-cart-sidebar')) {
            doNext = 'drawer';
          } else {
            doNext = 'page';
          }
        }

        if (doNext == 'page') {
          window.location.href = KROWN.settings.routes.cart_url;
          return;
        }

        if (document.getElementById('site-cart-sidebar')) {
          const sectionInnerHTML = new DOMParser().parseFromString(text, 'text/html');
          const cartFormInnerHTML = sectionInnerHTML.getElementById('AjaxCartForm')?.innerHTML || '';
          const cartSubtotalInnerHTML = sectionInnerHTML.getElementById('AjaxCartSubtotal')?.innerHTML || '';

          const cartItems = document.getElementById('AjaxCartForm');
          if (cartItems) {
            cartItems.innerHTML = cartFormInnerHTML;
            cartItems.ajaxifyCartItems?.();
          }

          document.querySelectorAll('[data-header-cart-count]').forEach((elm) => {
            const countElm = document.querySelector('#AjaxCartForm [data-cart-count]');
            if (countElm) elm.innerHTML = countElm.innerHTML;
          });

          if (document.getElementById('cart-recommendations-sidebar')) {
            document.getElementById('cart-recommendations-sidebar').generateRecommendations?.();
          }

          if (alert !== '' && cartItems) {
            cartItems.querySelector('form')?.prepend(alert);
          }

          const subtotal = document.getElementById('AjaxCartSubtotal');
          if (subtotal) subtotal.innerHTML = cartSubtotalInnerHTML;
        }

        if (doNext == 'drawer') {
          document.getElementById('site-cart-sidebar')?.show?.();
        } else {
          const miniCartPopup = document.getElementById('mini-cart-popup');
          if (!miniCartPopup) return;

          const single = miniCartPopup.querySelector('[data-js-mini-cart-single]');
          const plural = miniCartPopup.querySelector('[data-js-mini-cart-plural]');

          if (items == 1) {
            if (single) single.style.display = 'block';
            if (plural) plural.style.display = 'none';
          } else {
            if (plural) {
              plural.style.display = 'block';
              plural.innerHTML = KROWN.settings.locales.cart_added_items_count.replace('{{ count }}', items);
            }
            if (single) single.style.display = 'none';
          }

          miniCartPopup.show?.();

          if (miniCartPopup.dataset.hide != '0') {
            setTimeout(() => {
              miniCartPopup.hide?.();
            }, parseInt(miniCartPopup.dataset.hide, 10));
          }
        }
      })
      .catch((e) => {
        console.log(e);
      })
      .finally(() => {
        if (typeof callback === 'function') callback();
      });
  };
}

if (typeof QuickAddToCart !== 'function') {
  class QuickAddToCart extends HTMLElement {
    constructor() {
      super();
      if (this.querySelector('product-form')) this.init();
    }

    init() {
      this.querySelector('product-form').addEventListener('add-to-cart', () => {
        let doNext = '';

        if (document.body.classList.contains('template-cart')) {
          doNext = 'page';
        } else {
          if (KROWN.settings.cart_popup == 'true' && document.getElementById('mini-cart-popup')) {
            doNext = 'popup';
          } else if (KROWN.settings.cart_action == 'overlay' && document.getElementById('site-cart-sidebar')) {
            doNext = 'drawer';
          } else {
            doNext = 'page';
          }
        }

        if (doNext == 'page') {
          window.location.href = KROWN.settings.routes.cart_url;
          return;
        }

        if (doNext == 'drawer') {
          document.getElementById('site-cart-sidebar')?.show?.();
          return;
        }

        const miniCartPopup = document.getElementById('mini-cart-popup');
        if (!miniCartPopup) return;

        //miniCartPopup.querySelector('[data-js-mini-cart-single]')?.style && (miniCartPopup.querySelector('[data-js-mini-cart-single]').style.display = 'block');
        //miniCartPopup.querySelector('[data-js-mini-cart-plural]')?.style && (miniCartPopup.querySelector('[data-js-mini-cart-plural]').style.display = 'none');
        const singleElm = miniCartPopup.querySelector('[data-js-mini-cart-single]');
        const pluralElm = miniCartPopup.querySelector('[data-js-mini-cart-plural]');
        if (singleElm) singleElm.style.display = 'block';
        if (pluralElm) pluralElm.style.display = 'none';
        miniCartPopup.show?.();

        if (miniCartPopup.dataset.hide != '0') {
          setTimeout(() => {
            miniCartPopup.hide?.();
          }, parseInt(miniCartPopup.dataset.hide, 10));
        }
      });
    }
  }

  if (typeof customElements.get('quick-add-to-cart') === 'undefined') {
    customElements.define('quick-add-to-cart', QuickAddToCart);
  }
}

if (typeof QuickViewProduct !== 'function') {
  class QuickViewProduct extends HTMLElement {
    constructor() {
      super();
      if (this.querySelector('a')) this.init();
    }

    initModalProduct() {
      this.quickViewModal.querySelector('.product-quick-view__close')?.addEventListener('click', () => {
        this.quickViewModal.hide();
      });

      const form = this.quickViewModal.querySelector('[data-js-product-form]');
      if (form) {
        form.addEventListener('add-to-cart', () => {
          document.getElementById('site-cart-sidebar')?.scrollTo({ top: 0, behavior: 'smooth' });
          this.quickViewModal.hide();
        });
      }

      if (Shopify && Shopify.PaymentButton) {
        setTimeout(() => Shopify.PaymentButton.init(), 50);
      }

      const productVariants = this.quickViewModal.querySelector('product-variants');
      if (productVariants && !productVariants._defaultToFirstVariant) {
        productVariants
          .querySelectorAll('.product-variant__item--color [data-product-url][data-selected]')
          .forEach((elm) => {
            if (elm.hasAttribute('data-silent-selected')) return;

            const pvWrap = elm.closest('.product-variant');
            const label = pvWrap ? pvWrap.querySelector('.product-variant__item-text-label') : null;
            if (label) label.textContent = elm.value;
          });


        if (parseInt(productVariants.dataset.variants, 10) == 1) {
          productVariants.updateBuyButtons?.();
        }
      }
    }

    init() {
      this.quickViewModal = null;

      this.querySelector('a').addEventListener('click', (e) => {
        e.preventDefault();

        if (this.quickViewModal) {
          this.quickViewModal.show?.();
          return;
        }

        const target = e.currentTarget;
        target.classList.add('working');

        fetch(`${target.getAttribute('href')}${target.getAttribute('href').includes('?') ? '&' : '?'}view=quick-view`)
          .then((response) => response.text())
          .then((text) => {
            const doc = new DOMParser().parseFromString(text, 'text/html');
            const quickViewHTML = doc.querySelector('#product-quick-view');
            if (!quickViewHTML) return;

            const quickViewContainer = document.createElement('div');
            quickViewContainer.innerHTML = `
              <modal-box
                id="modal-${target.dataset.id}"
                class="modal modal--product"
                data-options='{"enabled": false,"showOnce": false,"blockTabNavigation": true}'
                tabindex="-1"
                role="dialog"
                aria-modal="true"
              >
                <div class="container--medium">
                  <div class="modal-content" data-js-product-page>
                    <button class="modal-close" data-js-close data-js-first-focus style="position:absolute;margin:0;top:0;right:0">
                      ${window.KROWN.settings.symbols.close}
                    </button>
                  </div>
                </div>
                <span class="modal-background" data-js-close></span>
              </modal-box>`;

            this.quickViewModal = quickViewContainer.querySelector('modal-box');
            document.body.appendChild(this.quickViewModal);

            const modalContent = this.quickViewModal.querySelector('.modal-content');
            if (modalContent) modalContent.innerHTML = quickViewHTML.innerHTML;

            setTimeout(() => this.initModalProduct(), 100);

            this.quickViewModal
              .querySelector('[data-js-product-page]')
              ?.addEventListener('reload', () => this.initModalProduct());

            if (!window.productPageScripts) {
              const scripts = this.quickViewModal.querySelectorAll('script[src]');
              scripts.forEach((elm) => {
                const script = document.createElement('script');
                script.src = elm.src;
                document.body.append(script);
              });
              window.productPageScripts = true;
            }

            setTimeout(() => {
              this.quickViewModal.show?.();
              target.classList.remove('working');
            }, 250);
          })
          .catch(() => {
            target.classList.remove('working');
          });
      });
    }
  }

  if (typeof customElements.get('quick-view-product') === 'undefined') {
    customElements.define('quick-view-product', QuickViewProduct);
  }
}

if (!KROWN.productItemUpdateHelper) {
  KROWN.productItemUpdateHelper = (productItem, productId, html, skeleton) => {
    const truncate = (str, max = 10) => {
      const array = str.trim().split(' ');
      const ellipsis = array.length > max ? '...' : '';
      return array.slice(0, max).join(' ') + ellipsis;
    };

    productItem.id = `product-item-${productId}`;

    // update thumbnail
    const newImage = html.querySelector('.product-item__image');
    if (newImage) productItem.querySelector('.product-item__image').innerHTML = newImage.innerHTML;

    productItem.querySelectorAll('.product-item__image img[sizes]').forEach((img) => {
      img.setAttribute('sizes', skeleton.settings.product_image_sizes);
    });

    // update inner content
    const textTarget = productItem.querySelector('.product-item__text');
    if (!textTarget) return;

    textTarget.innerHTML = '';

    skeleton.elements.forEach((elm) => {
      const classNames = elm.className;
      if (!classNames) return;

      const selector = '.' + classNames.split(' ').join('.');
      const newElement = html.querySelector(selector);

      if (!newElement) return;

      textTarget.append(newElement);

      if (classNames.includes('product-item__link')) {
        newElement.querySelector('.product-item__link .button__text').textContent =
          skeleton.settings.product_link_button_label;
      } else if (classNames.includes('product-item__excerpt')) {
        if (!(skeleton.settings.product_description_excerpt_link === true)) {
          newElement.querySelector('.product-item__excerpt-link')?.remove();
        }
        const excerpt = newElement.querySelector('.product-item__excerpt-text');
        if (excerpt) excerpt.textContent = truncate(excerpt.textContent, skeleton.settings.product_description_excerpt_length);
      }
    });

    // reset some settings
    if (skeleton.settings.defer_add == 'true') {
      productItem
        .querySelector('[data-js-product-add-to-cart]')
        ?.classList.remove(
          'button',
          'button--solid',
          'button--outline',
          'button--outline-hover-solid',
          'button--fullwidth',
          'button--loader',
          'button--move'
        );
    } else {
      productItem.querySelector('.defer-checkmark')?.remove();
    }

    if (skeleton.settings.show_add_to_cart_price && productItem.querySelector('quick-buy')) {
      productItem.querySelector('quick-buy').dataset.addToCartPrice = 'true';
    }

    if (skeleton.settings.unavailable_variants != 'hide') {
      const pv = productItem.querySelector('product-variants');
      if (!pv) return;

      if (skeleton.settings.unavailable_variants == 'show') {
        pv.setAttribute('data-hide-variants', 'true');
        productItem.querySelectorAll('[data-option-value-id]').forEach((elm) => elm.removeAttribute('disabled'));
      } else if (skeleton.settings.unavailable_variants == 'disable') {
        pv.setAttribute('data-hide-variants', 'disable');
      }

      pv.setAttribute('data-unavailable-variants', skeleton.settings.unavailable_variants);
      pv.classList.remove('product-variants--hide-unavailable');

      productItem.querySelector('.product-variant__out-of-stock')?.remove();
    }
  };
}
